import React, { useEffect, useState } from 'react';
import {
  FlexBox,
  FlexColumn,
  Input,
  InputValidationType,
  LabelFontSize,
} from '@make-software/cspr-design';
import styled from 'styled-components';
import FilterContainer, { FilterProps } from '../filter-container';
import { BodyText } from '@block-explorer/ui';
import { useFilterQuery } from '../filter-query';

const InputContainer = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    flexDirection: ['column', 'row'],
    alignItems: 'flex-start',
    paddingBottom: 8,
    '> * + *': {
      marginLeft: [0, 24],
      marginTop: [10, 0],
    },
    textTransform: 'none',
  })
);

interface InputFilterProps extends FilterProps {
  label: string;
  rightLabel?: string;
  placeholder: string;
  filterId: string;
  handleValidateInput: (value: string | null) => string | null;
  validationType?: InputValidationType;
  isFullView?: boolean | undefined;
}

export const InputFilter = ({
  filterId,
  filters,
  setFilters,
  label,
  rightLabel,
  placeholder,
  handleValidateInput,
  validationType,
  isFullView,
}: InputFilterProps) => {
  const { queryFilters, syncFiltersToQuery } = useFilterQuery();
  const initialInputValue = filters?.[filterId] || queryFilters?.[filterId];

  const [inputValue, setInputValue] = useState<string>(initialInputValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!queryFilters?.[filterId]) return;

    if (inputValue !== queryFilters?.[filterId]) {
      setInputValue(queryFilters?.[filterId]);
      setError(null);
    }

    const currentFilterValue = filters?.[filterId];
    if (currentFilterValue !== queryFilters?.[filterId]) {
      setFilters({
        ...(filters || {}),
        [filterId]: queryFilters?.[filterId],
      });
    }
  }, [queryFilters?.[filterId], filterId]);

  const handleChangeInputValue = (text) => {
    setError(null);
    setInputValue(text);
  };

  const handlePasteClick = async () => {
    const copiedText = await navigator.clipboard.readText();
    const error = handleValidateInput(copiedText);
    if (error) {
      setError(error);
    } else {
      setInputValue(copiedText);
    }
  };

  const handleSetFilters = () => {
    const error = handleValidateInput(inputValue);
    if (!error) {
      const newFilters = { ...(filters || {}), [filterId]: inputValue };
      setFilters(newFilters);
      syncFiltersToQuery(newFilters);
    }
    {
      setError(error);
    }
  };
  const handleResetFilters = () => {
    setInputValue('');
    setError(null);

    const { [filterId]: _, ...rest } = filters || {};
    setFilters(rest);
    syncFiltersToQuery(rest);
  };

  const isFilterSet = Object.keys(filters || {})?.includes(filterId);
  const disabled = !inputValue || !!error;

  return (
    <FilterContainer
      isFilterSet={isFilterSet}
      onSetFilters={handleSetFilters}
      onResetFilters={handleResetFilters}
      onResetErrors={() => setError(null)}
      disabled={disabled}
      isFullView={isFullView}
    >
      <FlexColumn itemsSpacing={4}>
        <InputContainer>
          <Input
            id={`${filterId}-input-filter`}
            style={{ flex: 1, width: '100%' }}
            onChange={(event) => {
              handleChangeInputValue(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSetFilters();
              }
            }}
            placeholder={placeholder}
            value={inputValue}
            validationType={validationType}
            label={
              <BodyText size={1} scale={'sm'} variation={'black'}>
                {label}
              </BodyText>
            }
            rightLabel={
              rightLabel ? (
                <BodyText
                  size={1}
                  scale={'sm'}
                  variation={'blue'}
                  onClick={handlePasteClick}
                  tabIndex={0}
                  onKeyDown={async (ev) => {
                    if (ev.key === 'Enter') await handlePasteClick();
                  }}
                >
                  {rightLabel}
                </BodyText>
              ) : null
            }
            labelFontSize={LabelFontSize.small}
            error={!!error}
            validationText={error}
            monotype
            autoFocus
          />
        </InputContainer>
      </FlexColumn>
    </FilterContainer>
  );
};
